import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../../../utilities/appToast';
import * as XLSX from 'xlsx';
import {
    postExcelPublicacionesModerador,
    putActualizacionMasivaBulkParcialApi,
    postMasivaImagenesExcelApi,
    getFormatoExcel
} from '../../../../api/axios';
import {
    buildActualizacionMasivaBulkParcialItems,
    validateTipoEnvioWizardRow,
    rowHasTipoEnvioBulkUpdatePayload
} from '../../../../utilities/tipoEnvioMasivaBulkParcial';
import { base64ToBlob, saveByteArrayXlsx } from '../../../../utilities/formatFile';
import { normalizeSkuFieldForApi } from '../../../../utilities/skuApiNormalize';
import './CreacionMasivaWizard.css';
import './ActualizacionMasivaWizard.css';

const KIND_PRECIO = 'precio';
const KIND_TIPO = 'tipo';
const KIND_IMAGENES = 'imagenes';

/** Misma secuencia que CreacionMasivaWizard */
const STEP_LABELS = ['Tipo de actualización', 'Lote de actualización', 'Revision', 'Resultado'];
const ROWS_PER_PAGE = 25;

const createRowId = () => `u-${Date.now()}-${Math.round(Math.random() * 100000)}`;

const KIND_META = {
    [KIND_PRECIO]: {
        title: 'Precio y stock',
        subtitle: 'SKU, precio lista, precio promo y stock.'
    },
    [KIND_TIPO]: {
        title: 'Publicación',
        subtitle:
            'Actualización parcial: una fila puede llevar solo uno o varios datos a cambiar (sin obligatorios del alta). IdPublicacion identifica la publicación.'
    },
    [KIND_IMAGENES]: {
        title: 'Imágenes',
        subtitle: 'IdPublicacion e Img1 a Img5 (URLs o rutas).'
    }
};

const TEMPLATE_META_BY_KIND = {
    [KIND_PRECIO]: { tipoExcel: 1, downloadName: 'Modelo Actualización Precio-Stock' },
    [KIND_IMAGENES]: { tipoExcel: 2, downloadName: 'Modelo Actualización Masiva Imágenes' },
    [KIND_TIPO]: { tipoExcel: 3, downloadName: 'Modelo Actualización Publicación' }
};

const normalizeHeader = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase()
        .replace(/[\s_.-]+/g, '');

const formatApiValue = (value, fallback = '') => {
    const formatted = value == null || value === '' ? '' : String(value).trim();
    return formatted || fallback;
};

const toDecimalOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return v;
    const n = Number(String(v).trim().replace(',', '.'));
    return Number.isNaN(n) ? null : n;
};

const toIntOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.parseInt(v, 10);
    const n = Number(String(v).trim().replace(',', '.'));
    return Number.isNaN(n) ? null : Number.parseInt(n, 10);
};

const normalizeKey = (s) => String(s ?? '').trim().toUpperCase();

/** Clave estable para cruzar errores de tipo/envío: id sin tocar; SKUs con punto decimal. */
const tipoIdentifierMatchRaw = (rowLike) => {
    const c = rowLike?.codProducto ?? rowLike?.CodProducto;
    const ip = rowLike?.idPublicacion ?? rowLike?.IdPublicacion;
    const ss = normalizeSkuFieldForApi(rowLike?.skuSeller ?? rowLike?.SkuSeller);
    const sm = normalizeSkuFieldForApi(rowLike?.skuMarket ?? rowLike?.SkuMarket);
    const pick = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
    return pick(c) || pick(ip) || pick(ss) || pick(sm);
};

const createEmptyRowPrecio = (source = 'manual') => ({
    id: createRowId(),
    source,
    skuMarket: '',
    precioLista: '',
    precioPromocional: '',
    stock: '',
    errors: {}
});

const createEmptyRowImagenes = (source = 'manual') => ({
    id: createRowId(),
    source,
    idPublicacion: '',
    img1: '',
    img2: '',
    img3: '',
    img4: '',
    img5: '',
    errors: {}
});

const createEmptyRowTipo = (source = 'manual') => ({
    id: createRowId(),
    source,
    codProducto: '',
    skuSeller: '',
    skuMarket: '',
    observacion: '',
    titulo: '',
    ean: '',
    linkYoutube: '',
    descripcionAmpliada: '',
    idTipoPublicacion: '',
    envio: '',
    envioGratis: '',
    categoria: '',
    marca: '',
    idMotivoRevision: '',
    observacionesRevision: '',
    envioPropio: '',
    codigoLegibilidad: '',
    garantiaExtActiva: '',
    garantiaFabrica: '',
    promesaEntrega: '',
    amba: '',
    interior: '',
    patagonia: '',
    errors: {}
});

const rowFromNormalizedCells = (_norm, kind) => {
    if (kind === KIND_PRECIO) {
        return createEmptyRowPrecio('csv');
    }
    if (kind === KIND_IMAGENES) {
        return createEmptyRowImagenes('csv');
    }
    return createEmptyRowTipo('csv');
};

const fillPrecioFromNorm = (row, obj) => ({
    ...row,
    skuMarket: normalizeSkuFieldForApi(obj.sku ?? obj.skumarket ?? obj.skumarketplace ?? ''),
    precioLista: String(obj.preciolista ?? '').trim(),
    precioPromocional: String(obj.preciopromo ?? '').trim(),
    stock: String(obj.stock ?? '').trim()
});

const fillImagenesFromNorm = (row, obj) => ({
    ...row,
    idPublicacion: String(obj.idpublicacion ?? '').trim(),
    img1: String(obj.img1 ?? '').trim(),
    img2: String(obj.img2 ?? '').trim(),
    img3: String(obj.img3 ?? '').trim(),
    img4: String(obj.img4 ?? '').trim(),
    img5: String(obj.img5 ?? '').trim()
});

const fillTipoFromNorm = (row, obj) => ({
    ...row,
    codProducto: String(obj.codproducto ?? obj.idpublicacion ?? '').trim(),
    skuSeller: normalizeSkuFieldForApi(obj.skuseller ?? ''),
    skuMarket: normalizeSkuFieldForApi(obj.skumarket ?? obj.skumarkeet ?? obj.sku ?? ''),
    observacion: String(obj.observacion ?? '').trim(),
    titulo: String(obj.titulo ?? '').trim(),
    ean: String(obj.ean ?? '').trim(),
    linkYoutube: String(obj.linkyoutube ?? '').trim(),
    descripcionAmpliada: String(obj.descripcionampliada ?? '').trim(),
    idTipoPublicacion: String(obj.idtipopublicacion ?? '').trim(),
    envio: String(obj.envio ?? obj.tipoentrega ?? '').trim(),
    envioGratis: String(obj.enviogratis ?? '').trim(),
    categoria: String(obj.categoria ?? '').trim(),
    marca: String(obj.marca ?? '').trim(),
    idMotivoRevision: String(obj.idmotivorevision ?? '').trim(),
    observacionesRevision: String(obj.observacionesrevision ?? '').trim(),
    envioPropio: String(obj.enviopropio ?? '').trim(),
    codigoLegibilidad: String(obj.codigolegibilidad ?? '').trim(),
    garantiaExtActiva: String(obj.garantiaextactiva ?? '').trim(),
    garantiaFabrica: String(obj.garantiafabrica ?? '').trim(),
    promesaEntrega: String(obj.promesaentrega ?? '').trim(),
    amba: String(obj.amba ?? '').trim(),
    interior: String(obj.interior ?? '').trim(),
    patagonia: String(obj.patagonia ?? '').trim()
});

const parseRowFromNormObject = (norm, kind) => {
    let base = rowFromNormalizedCells(norm, kind);
    if (kind === KIND_PRECIO) return fillPrecioFromNorm(base, norm);
    if (kind === KIND_IMAGENES) return fillImagenesFromNorm(base, norm);
    return fillTipoFromNorm(base, norm);
};

const isRowBlankPrecio = (row) =>
    !String(row?.skuMarket ?? '').trim() &&
    !String(row?.precioLista ?? '').trim() &&
    !String(row?.precioPromocional ?? '').trim() &&
    String(row?.stock ?? '').trim() === '';

const isRowBlankImagenes = (row) =>
    !String(row?.idPublicacion ?? '').trim() &&
    ![row?.img1, row?.img2, row?.img3, row?.img4, row?.img5].some((x) => String(x ?? '').trim());

const isRowBlankTipo = (row) => {
    const hasIdentifier =
        String(row?.codProducto ?? '').trim() ||
        String(row?.skuSeller ?? '').trim() ||
        String(row?.skuMarket ?? '').trim();
    if (!hasIdentifier) return true;
    return !rowHasTipoEnvioBulkUpdatePayload(row);
};

const isRowBlank = (row, kind) => {
    if (kind === KIND_PRECIO) return isRowBlankPrecio(row);
    if (kind === KIND_IMAGENES) return isRowBlankImagenes(row);
    return isRowBlankTipo(row);
};

const getRowErrorsPrecio = (row, flags = { precioLista: true, promo: true, stock: true }) => {
    const errors = {};
    const sku = String(row?.skuMarket ?? '').trim();
    if (!sku) errors.skuMarket = 'Ingresá el SKU (SKU market).';
    const pl = flags.precioLista ? toDecimalOrNull(row?.precioLista) : null;
    const pp =
        flags.promo && row?.precioPromocional !== '' && row?.precioPromocional != null
            ? toDecimalOrNull(row?.precioPromocional)
            : null;
    const st = flags.stock ? (row?.stock === '' || row?.stock == null ? null : toIntOrNull(row?.stock)) : null;
    const hasAny = pl != null || pp != null || st != null;
    if (sku && !hasAny && (flags.precioLista || flags.promo || flags.stock)) {
        errors.precioLista = 'Completá al menos un campo entre precio lista, precio promo o stock (según lo marcado en contexto).';
    }
    if (flags.precioLista && pl != null && pl < 0) errors.precioLista = 'Precio lista inválido.';
    if (flags.promo && pp != null && pp < 0) errors.precioPromocional = 'Precio promo inválido.';
    if (flags.precioLista && flags.promo && pl != null && pp != null && pp > pl) {
        errors.precioPromocional = 'Precio promo no puede superar precio lista.';
    }
    if (flags.stock && st != null && st < 0) errors.stock = 'Stock inválido.';
    return errors;
};

const getRowErrorsImagenes = (row) => {
    const errors = {};
    const id = String(row?.idPublicacion ?? '').trim();
    if (!id) errors.idPublicacion = 'Ingresá IdPublicacion.';
    else if (!Number.isFinite(toIntOrNull(id)) || toIntOrNull(id) < 1) errors.idPublicacion = 'IdPublicacion debe ser un entero mayor a 0.';
    const imgs = [row?.img1, row?.img2, row?.img3, row?.img4, row?.img5].map((x) => String(x ?? '').trim()).filter(Boolean);
    if (imgs.length === 0) errors.img1 = 'Indicá al menos una imagen (Img1 a Img5).';
    return errors;
};

const getRowErrorsTipo = (row) => validateTipoEnvioWizardRow(row);

const getRowErrorsForKind = (row, kind, precioFlags) => {
    if (kind === KIND_PRECIO) {
        return getRowErrorsPrecio(row, precioFlags ?? { precioLista: true, promo: true, stock: true });
    }
    if (kind === KIND_IMAGENES) return getRowErrorsImagenes(row);
    return getRowErrorsTipo(row);
};

const getLiveRowErrors = (rows, kind, precioFlags) =>
    rows.map((row) => getRowErrorsForKind(row, kind, precioFlags));

const getRowsMeta = (rowsSnapshot, kind) =>
    (Array.isArray(rowsSnapshot) ? rowsSnapshot : []).map((row, index) => ({
        rowId: row?.id ?? null,
        rowNumber: index + 1,
        keyPrecio: kind === KIND_PRECIO ? normalizeKey(normalizeSkuFieldForApi(row?.skuMarket)) : '',
        keyImg: kind === KIND_IMAGENES ? normalizeKey(row?.idPublicacion) : '',
        keyTipo: kind === KIND_TIPO ? normalizeKey(tipoIdentifierMatchRaw(row)) : ''
    }));

const getErrorMatchKey = (item, kind) => {
    if (kind === KIND_PRECIO) {
        return normalizeKey(
            normalizeSkuFieldForApi(item?.skuMarket ?? item?.SkuMarket ?? item?.sku ?? item?.SKU)
        );
    }
    if (kind === KIND_IMAGENES) {
        return normalizeKey(item?.idPublicacion ?? item?.IdPublicacion);
    }
    return normalizeKey(tipoIdentifierMatchRaw(item));
};

const getRowLabelForKind = (row, kind) => {
    if (kind === KIND_PRECIO) return formatApiValue(row?.skuMarket, '');
    if (kind === KIND_IMAGENES) return formatApiValue(row?.idPublicacion, '');
    return formatApiValue(
        row?.codProducto || row?.idPublicacion || row?.skuSeller || row?.skuMarket,
        ''
    );
};

const getTipoIdentifierLabel = (row) => {
    const idPublicacion = formatApiValue(row?.codProducto || row?.idPublicacion, '');
    if (idPublicacion) return `IdPublicacion ${idPublicacion}`;
    const skuSeller = formatApiValue(row?.skuSeller, '');
    if (skuSeller) return `SkuSeller ${skuSeller}`;
    const skuMarket = formatApiValue(row?.skuMarket, '');
    if (skuMarket) return `SkuMarket ${skuMarket}`;
    return 'Sin identificador';
};

const getTipoChangedFieldsLabel = (row) => {
    const fields = [
        ['observacion', 'Observación'],
        ['titulo', 'Título'],
        ['ean', 'EAN'],
        ['linkYoutube', 'LinkYoutube'],
        ['descripcionAmpliada', 'DescripciónAmpliada'],
        ['idTipoPublicacion', 'IdTipoPublicacion'],
        ['envio', 'Envio'],
        ['envioGratis', 'EnvioGratis'],
        ['categoria', 'Categoria'],
        ['marca', 'Marca'],
        ['idMotivoRevision', 'IdMotivoRevision'],
        ['observacionesRevision', 'ObservacionesRevision'],
        ['envioPropio', 'EnvioPropio'],
        ['codigoLegibilidad', 'CodigoLegibilidad'],
        ['garantiaExtActiva', 'GarantiaExtActiva'],
        ['garantiaFabrica', 'GarantiaFabrica'],
        ['promesaEntrega', 'PromesaEntrega'],
        ['amba', 'Amba'],
        ['interior', 'Interior'],
        ['patagonia', 'Patagonia']
    ];
    const changed = fields
        .filter(([key]) => {
            const v = row?.[key];
            return v !== null && v !== undefined && String(v).trim() !== '';
        })
        .map(([, label]) => label);
    return changed.join(', ');
};

const enrichUpdateApiErrors = (items, rowsSnapshot, kind, createdItems = []) => {
    const rowsMeta = getRowsMeta(rowsSnapshot, kind);
    const availableIndexes = rowsMeta.map((_, i) => i);
    const createdKeys = new Set(
        (Array.isArray(createdItems) ? createdItems : [])
            .map((it) => getErrorMatchKey(it, kind))
            .filter((k) => k !== '')
    );
    if (createdKeys.size > 0) {
        for (let i = availableIndexes.length - 1; i >= 0; i -= 1) {
            const idx = availableIndexes[i];
            const meta = rowsMeta[idx];
            const k =
                kind === KIND_PRECIO ? meta.keyPrecio : kind === KIND_IMAGENES ? meta.keyImg : meta.keyTipo;
            if (createdKeys.has(k)) availableIndexes.splice(i, 1);
        }
    }

    return (Array.isArray(items) ? items : []).map((rawItem, index) => {
        const baseItem =
            rawItem && typeof rawItem === 'object' && !Array.isArray(rawItem)
                ? { ...rawItem }
                : { message: rawItem };
        let matchedIndex = -1;
        let matchType = null;

        const explicitRow = Number.parseInt(
            String(baseItem?.rowNumber ?? baseItem?.RowNumber ?? baseItem?.fila ?? baseItem?.Fila ?? ''),
            10
        );
        if (Number.isFinite(explicitRow) && explicitRow >= 1 && explicitRow <= rowsMeta.length) {
            matchedIndex = explicitRow - 1;
            matchType = 'rowNumber';
        } else {
            const cand = getErrorMatchKey(baseItem, kind);
            if (cand !== '') {
                matchedIndex = rowsMeta.findIndex((m) => {
                    const k =
                        kind === KIND_PRECIO ? m.keyPrecio : kind === KIND_IMAGENES ? m.keyImg : m.keyTipo;
                    return k === cand;
                });
                if (matchedIndex >= 0) matchType = kind === KIND_PRECIO ? 'sku' : 'key';
            }
        }
        if (matchedIndex === -1) {
            matchedIndex = availableIndexes[index] ?? availableIndexes[0] ?? index;
            if (rowsMeta[matchedIndex]) matchType = 'inferred';
        }
        const pos = availableIndexes.indexOf(matchedIndex);
        if (pos >= 0) availableIndexes.splice(pos, 1);
        const rowMeta = rowsMeta[matchedIndex];
        return {
            ...baseItem,
            __rowId: rowMeta?.rowId ?? null,
            __rowNumber: rowMeta?.rowNumber ?? null,
            __rowKey: getRowLabelForKind(rowsSnapshot[matchedIndex], kind),
            __rowMatchType: matchType
        };
    });
};

const getResultErrorHeading = (item, index) => {
    const labelBase = item?.__rowNumber
        ? `Fila ${item.__rowNumber}${item?.__rowMatchType === 'inferred' ? ' (estimada)' : ''}`
        : `Error ${index + 1}`;
    const key = formatApiValue(item?.__rowKey, '');
    return key ? `${labelBase} · ${key}` : labelBase;
};

const buildPayloadPorCodigo = (idSeller, rows, flags) => ({
    idSeller,
    listaElementos: rows.map((item) => ({
        skuMarket: normalizeSkuFieldForApi(item.skuMarket ?? ''),
        precioLista: flags.precioLista ? toDecimalOrNull(item.precioLista) : null,
        precioPromocional: flags.promo ? toDecimalOrNull(item.precioPromocional) : null,
        stock: flags.stock ? toIntOrNull(item.stock) : null
    }))
});

const buildWorkbookImagenes = (rows) => {
    const data = rows.map((r) => ({
        IdPublicacion: toIntOrNull(r.idPublicacion) ?? String(r.idPublicacion ?? '').trim(),
        Img1: String(r.img1 ?? '').trim(),
        Img2: String(r.img2 ?? '').trim(),
        Img3: String(r.img3 ?? '').trim(),
        Img4: String(r.img4 ?? '').trim(),
        Img5: String(r.img5 ?? '').trim()
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Hoja1');
    return wb;
};

const workbookToBase64 = (wb) => XLSX.write(wb, { bookType: 'xlsx', type: 'base64' });

const detectCsvDelimiter = (headerLine) => {
    if (headerLine.includes('\t')) return '\t';
    const semi = (headerLine.match(/;/g) || []).length;
    const comma = (headerLine.match(/,/g) || []).length;
    if (semi === 0 && comma === 0) return ';';
    return semi >= comma ? ';' : ',';
};

const parsePasteToRows = (text, kind) => {
    const lines = String(text ?? '')
        .trim()
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length < 2) return [];
    const delim = detectCsvDelimiter(lines[0]);
    const headerCells = lines[0].split(delim).map((c) => c.trim());
    const headers = headerCells.map((h) => normalizeHeader(h));
    const out = [];
    for (let i = 1; i < lines.length; i += 1) {
        const cells = lines[i].split(delim);
        const norm = {};
        headers.forEach((h, idx) => {
            norm[h] = (cells[idx] ?? '').trim();
        });
        const row = parseRowFromNormObject(norm, kind);
        if (!isRowBlank(row, kind)) out.push(row);
    }
    return out;
};

/** Pegado/CSV tipo-envío: avisa cuántas filas del texto no entraron a la actualización (vacías de contenido útil o solo Id). */
const toastTipoPasteSkippedLines = (updateKind, text, parsedLen) => {
    if (updateKind !== KIND_TIPO) return;
    const lines = String(text ?? '')
        .trim()
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
    const bodyLines = Math.max(0, lines.length - 1);
    const skipped = bodyLines - parsedLen;
    if (skipped > 0) {
        toast.info(
            `${skipped} fila(s) del texto no se agregaron a la actualización (sin identificador: IdPublicacion/SkuSeller/SkuMarket, o sin datos a cambiar).`
        );
    }
};

const cloneRowsSnapshot = (list) =>
    (Array.isArray(list) ? list : []).map((row) => ({
        ...row,
        errors: { ...(row?.errors ?? {}) }
    }));

const extractCreatedArraysFromUpdateResponse = (data) => {
    if (!data || typeof data !== 'object') return [];
    const keys = ['publicaciones', 'Publicaciones', 'actualizadas', 'listaActualizadas', 'publicacion', 'listaOk', 'ok'];
    for (let i = 0; i < keys.length; i += 1) {
        const arr = data[keys[i]];
        if (Array.isArray(arr)) return arr;
    }
    return [];
};

const implicitCreatedRowsFromSnapshot = (rowsSnapshot, enrichedErrors, kind) => {
    const failedIds = new Set((Array.isArray(enrichedErrors) ? enrichedErrors : []).map((e) => e?.__rowId).filter(Boolean));
    return (Array.isArray(rowsSnapshot) ? rowsSnapshot : [])
        .filter((row) => !failedIds.has(row.id))
        .map((row) => ({
            label: getRowLabelForKind(row, kind),
            skuMarket: row.skuMarket,
            idPublicacion: row.idPublicacion,
            codProducto: row.codProducto
        }));
};

const flattenBulkParcialErrorsList = (responseData) => {
    const d = responseData ?? {};
    const errorsArr = Array.isArray(d.errors) ? d.errors : [];
    const erroresArr = Array.isArray(d.errores) ? d.errores : [];
    const tupleErrors = erroresArr
        .filter((x) => x && typeof x === 'object' && !Array.isArray(x) && ('item1' in x || 'Item1' in x))
        .map((x) => ({
            errorMesage: [x.item1 ?? x.Item1, x.item2 ?? x.Item2].filter(Boolean).join(' — ')
        }));
    const stringErrors = erroresArr
        .filter((x) => typeof x === 'string')
        .map((s) => ({ errorMesage: s }));
    const otherErrores = erroresArr.filter(
        (x) =>
            x &&
            typeof x === 'object' &&
            !Array.isArray(x) &&
            !('item1' in x) &&
            !('Item1' in x)
    );
    return [...errorsArr, ...tupleErrors, ...stringErrors, ...otherErrores];
};

const mapExplicitCreatedLabels = (explicitCreated) =>
    explicitCreated.map((item) =>
        typeof item === 'object' && item && !Array.isArray(item)
            ? {
                  ...item,
                  label: formatApiValue(
                      item?.skuMarket ??
                          item?.SkuMarket ??
                          item?.idPublicacion ??
                          item?.IdPublicacion ??
                          item?.codProducto ??
                          item?.CodProducto,
                      'OK'
                  )
              }
            : { label: formatApiValue(item, 'OK') }
    );

const normalizeTipoEnvioImagenResult = (rowsSnapshot, kind, responseData) => {
    const d = responseData ?? {};
    const list = flattenBulkParcialErrorsList(d);
    const explicitCreated = extractCreatedArraysFromUpdateResponse(d);

    if (list.length === 0) {
        if (explicitCreated.length > 0) {
            return {
                created: mapExplicitCreatedLabels(explicitCreated),
                errors: []
            };
        }
        return {
            created: (Array.isArray(rowsSnapshot) ? rowsSnapshot : []).map((row) => ({
                label: getRowLabelForKind(row, kind)
            })),
            errors: []
        };
    }
    const enriched = enrichUpdateApiErrors(list, rowsSnapshot, kind, []);
    const created = explicitCreated.length
        ? mapExplicitCreatedLabels(explicitCreated)
        : implicitCreatedRowsFromSnapshot(rowsSnapshot, enriched, kind);
    return {
        created,
        errors: enriched
    };
};

const toastActualizacionBatchSummary = (createdCount, errorCount, kindLabel) => {
    const suffix = kindLabel ? ` — ${kindLabel}` : '';
    if (createdCount > 0 && errorCount === 0) {
        toast.success(`${createdCount} fila(s) procesada(s) correctamente${suffix}.`);
        return;
    }
    if (createdCount > 0 && errorCount > 0) {
        toast.success(`${createdCount} fila(s) correctas${suffix}.`);
        toast.warn(`${errorCount} fila(s) con error${suffix}. Detalle en Resultado.`);
        return;
    }
    if (errorCount > 0) {
        toast.warn(`Ninguna fila OK${suffix}. ${errorCount} error(es). Detalle en Resultado.`);
        return;
    }
    toast.success(`Proceso finalizado${suffix}.`);
};

const ActualizacionMasivaWizard = ({ idSeller, userType, idUsuario, tipoUsuario }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [updateKind, setUpdateKind] = useState('');
    const [rows, setRows] = useState([]);
    const [pasteValue, setPasteValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentRowsPage, setCurrentRowsPage] = useState(1);
    const [result, setResult] = useState({ created: [], errors: [] });
    const [highlightErrorRowIds, setHighlightErrorRowIds] = useState([]);

    const csvInputRef = useRef(null);
    const [csvDropZoneKey, setCsvDropZoneKey] = useState(0);
    const [csvDragDepth, setCsvDragDepth] = useState(0);
    const [csvJustLoaded, setCsvJustLoaded] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);

    const [chStock, setChStock] = useState(true);
    const [chPrecioLista, setChPrecioLista] = useState(true);
    const [chPrecioPromo, setChPrecioPromo] = useState(true);

    const isSeller = userType?.isSeller || userType?.isSellerAdmin;
    const isAdmin = userType?.isCategory || userType?.isAdminMod;
    const canTipoEnvio = isAdmin || isSeller;
    const isSellerOrAdmin =
        userType?.isSellerAdmin || userType?.isSeller || userType?.isAdmin || userType?.isAdminMod;

    const kindOptions = useMemo(() => {
        const all = [
            { id: KIND_PRECIO, ...KIND_META[KIND_PRECIO] },
            { id: KIND_TIPO, ...KIND_META[KIND_TIPO], disabled: !canTipoEnvio },
            { id: KIND_IMAGENES, ...KIND_META[KIND_IMAGENES] }
        ];
        return all;
    }, [canTipoEnvio]);

    const meta = updateKind ? KIND_META[updateKind] : null;

    const precioFlags = useMemo(
        () => ({
            precioLista: chPrecioLista,
            promo: chPrecioPromo,
            stock: chStock
        }),
        [chPrecioLista, chPrecioPromo, chStock]
    );

    const effectivePrecioFlags = isSellerOrAdmin ? precioFlags : { precioLista: true, promo: true, stock: true };

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
        setCurrentRowsPage((p) => Math.min(p, totalPages));
    }, [rows.length]);

    const resetWizard = useCallback(() => {
        setStep(0);
        setUpdateKind('');
        setRows([]);
        setPasteValue('');
        setCurrentRowsPage(1);
        setResult({ created: [], errors: [] });
        setHighlightErrorRowIds([]);
        setChStock(true);
        setChPrecioLista(true);
        setChPrecioPromo(true);
        setCsvDropZoneKey((k) => k + 1);
        setCsvDragDepth(0);
        setCsvJustLoaded(false);
        setShowTemplateModal(false);
    }, []);

    const downloadTemplateByKind = useCallback(async (kind) => {
        const cfg = TEMPLATE_META_BY_KIND[kind];
        if (!cfg?.tipoExcel) {
            toast.warn('No encontramos formato para este tipo de actualización.');
            return;
        }
        try {
            const { data } = await getFormatoExcel({ tipoExcel: cfg.tipoExcel });
            let b64 = data?.excel ?? data?.archivoBase64 ?? data?.base64 ?? (typeof data === 'string' ? data : '');
            if (!b64) throw new Error('Respuesta sin base64');
            const comma = b64.indexOf(',');
            if (comma !== -1) b64 = b64.slice(comma + 1);
            b64 = b64.replace(/-/g, '+').replace(/_/g, '/');
            while (b64.length % 4 !== 0) b64 += '=';
            const isCsvTemplate = [KIND_PRECIO, KIND_IMAGENES, KIND_TIPO].includes(kind);
            const blob = base64ToBlob(
                b64,
                isCsvTemplate ? 'text/csv;charset=utf-8;' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            );
            const fileName = isCsvTemplate
                ? (cfg.downloadName.endsWith('.csv') ? cfg.downloadName : `${cfg.downloadName}.csv`)
                : (cfg.downloadName.endsWith('.xlsx') ? cfg.downloadName : `${cfg.downloadName}.xlsx`);
            saveByteArrayXlsx(fileName, blob);
        } catch (e) {
            console.error(e);
            toast.error('No se pudo descargar el formato de Excel.');
        }
    }, []);

    const selectUpdateKind = useCallback((kind) => {
        setUpdateKind(kind);
        setShowTemplateModal(true);
    }, []);

    const closeTemplateModalAndContinue = useCallback(() => {
        setShowTemplateModal(false);
        setStep(1);
    }, []);

    const bumpCsvDropZone = useCallback(() => {
        setCsvDropZoneKey((k) => k + 1);
    }, []);

    const flashCsvLoaded = useCallback(() => {
        setCsvJustLoaded(true);
        window.setTimeout(() => setCsvJustLoaded(false), 700);
    }, []);

    const applyCsvTextContent = useCallback(
        (text, sourceLabel = 'CSV') => {
            if (!updateKind) {
                toast.warn('Elegí primero el tipo de actualización en el paso anterior.');
                return;
            }
            const parsed = parsePasteToRows(text, updateKind);
            if (!parsed.length) {
                toast.warn('No se pudieron leer filas del archivo. Verificá encabezados en la primera fila.');
                return;
            }
            toastTipoPasteSkippedLines(updateKind, text, parsed.length);
            setRows((prev) => [...prev, ...parsed]);
            toast.success(`${parsed.length} fila(s) agregadas desde ${sourceLabel}.`);
            bumpCsvDropZone();
            flashCsvLoaded();
        },
        [updateKind, bumpCsvDropZone, flashCsvLoaded]
    );

    const readCsvFile = useCallback(
        (file) => {
            if (!file) return;
            const name = String(file.name || '').toLowerCase();
            if (!name.endsWith('.csv')) {
                toast.warn(
                    'Subí un archivo .csv (podés exportarlo desde Excel como CSV separado por comas o punto y coma).'
                );
                return;
            }
            const reader = new FileReader();
            reader.onload = (ev) => {
                const text = ev.target?.result;
                applyCsvTextContent(text, 'CSV');
            };
            reader.onerror = () => toast.error('No se pudo leer el archivo.');
            reader.readAsText(file, 'UTF-8');
        },
        [applyCsvTextContent]
    );

    const onCsvDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setCsvDragDepth(0);
            const file = e.dataTransfer?.files?.[0];
            readCsvFile(file);
        },
        [readCsvFile]
    );

    const onCsvDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setCsvDragDepth((d) => d + 1);
    }, []);

    const onCsvDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setCsvDragDepth((d) => Math.max(0, d - 1));
    }, []);

    const onCsvInputChange = useCallback(
        (e) => {
            const file = e.target?.files?.[0];
            readCsvFile(file);
            e.target.value = '';
        },
        [readCsvFile]
    );

    const csvDragging = csvDragDepth > 0;

    const addEmptyRow = () => {
        if (!updateKind) return;
        const factories = {
            [KIND_PRECIO]: createEmptyRowPrecio,
            [KIND_IMAGENES]: createEmptyRowImagenes,
            [KIND_TIPO]: createEmptyRowTipo
        };
        setRows((prev) => [...prev, factories[updateKind]()]);
    };

    const removeRow = (rowId) => {
        setRows((prev) => prev.filter((r) => r.id !== rowId));
        setHighlightErrorRowIds((prev) => prev.filter((id) => id !== rowId));
    };

    const handleRowChange = (rowId, field, value) => {
        setHighlightErrorRowIds((prev) => prev.filter((id) => id !== rowId));
        setRows((prev) =>
            prev.map((row) =>
                row.id === rowId
                    ? {
                          ...row,
                          [field]: value,
                          errors: { ...row.errors, [field]: undefined, ...(field === 'img1' ? { img1: undefined } : {}) }
                      }
                    : row
            )
        );
    };

    const applyRowErrors = (rowErrors) => {
        setRows((prev) => prev.map((row, i) => ({ ...row, errors: rowErrors[i] ?? {} })));
    };

    const validateRowsStep = () => {
        if (!updateKind || rows.length === 0) return [];
        const rowErrors = getLiveRowErrors(
            rows,
            updateKind,
            updateKind === KIND_PRECIO ? effectivePrecioFlags : undefined
        );
        applyRowErrors(rowErrors);
        return rowErrors;
    };

    const prepareReview = () => {
        if (!updateKind) {
            toast.warn('Elegí un tipo de actualización.');
            return false;
        }
        if (updateKind === KIND_PRECIO && isSellerOrAdmin && !String(idSeller ?? '').trim()) {
            toast.warn('No hay seller asociado para enviar la actualización.');
            return false;
        }
        if (
            updateKind === KIND_PRECIO &&
            isSellerOrAdmin &&
            !chPrecioLista &&
            !chPrecioPromo &&
            !chStock
        ) {
            toast.warn('Marcá al menos un campo a actualizar: precio lista, precio promo o stock.');
            return false;
        }
        if (rows.length === 0) {
            toast.warn('Agregá al menos una fila a la actualización.');
            return false;
        }
        const rowErrors = validateRowsStep();
        const hasErr = rowErrors.some((e) => Object.keys(e).length > 0);
        if (hasErr) {
            toast.warn('Revisá las filas marcadas antes de continuar.');
            return false;
        }
        return true;
    };

    const applyPaste = () => {
        if (!pasteValue.trim() || !updateKind) {
            toast.warn('Pegá texto con encabezados y filas (separador ; o tab).');
            return;
        }
        const parsed = parsePasteToRows(pasteValue, updateKind);
        if (!parsed.length) {
            toast.warn('No se pudieron interpretar filas. Incluí la fila de encabezados.');
            return;
        }
        toastTipoPasteSkippedLines(updateKind, pasteValue, parsed.length);
        setRows((prev) => [...prev, ...parsed]);
        setPasteValue('');
        toast.success(`${parsed.length} fila(s) agregadas desde el portapapeles.`);
    };

    const clearRowsLote = () => {
        setRows([]);
        setPasteValue('');
        setResult({ created: [], errors: [] });
        setStep(1);
        toast.info('Se vació la actualización.');
    };

    const removeCsvRows = () => {
        const remaining = rows.filter((r) => r?.source !== 'csv');
        if (remaining.length === rows.length) {
            toast.info('No hay filas agregadas por pegado para eliminar.');
            return;
        }
        setRows(remaining);
        setResult({ created: [], errors: [] });
        toast.info('Se quitaron las filas cargadas por pegado.');
    };

    const returnToRowsForCorrection = () => {
        const errorResults = Array.isArray(result?.errors) ? result.errors : [];
        const failedIds = errorResults.map((item) => item?.__rowId).filter(Boolean);
        const inferredMatch = errorResults.some((item) => item?.__rowMatchType === 'inferred');
        const createdResults = Array.isArray(result?.created) ? result.created : [];
        const canKeepOnlyFailed = failedIds.length > 0 && (!inferredMatch || createdResults.length === 0);
        const nextRows = canKeepOnlyFailed ? rows.filter((row) => failedIds.includes(row.id)) : rows;
        const firstNum = errorResults.map((item) => item?.__rowNumber).find((n) => Number.isFinite(n));
        setRows(nextRows);
        setCurrentRowsPage(firstNum ? Math.max(1, Math.ceil(firstNum / ROWS_PER_PAGE)) : 1);
        setResult({ created: [], errors: [] });
        setHighlightErrorRowIds(failedIds);
        setStep(1);
        window.setTimeout(() => {
            const anchorId = failedIds[0];
            if (!anchorId) return;
            const rowEl = document.querySelector(`[data-row-id="${anchorId}"]`);
            if (!rowEl) return;
            rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
        if (canKeepOnlyFailed) {
            toast.info('Quedaron solo las filas con error para corregir y reenviar.');
        } else {
            toast.info('Volvimos a la actualización completa. Algunos errores pueden estar estimados por fila.');
        }
    };

    const handleNext = () => {
        if (step === 0) {
            if (!updateKind) {
                toast.warn('Elegí qué tipo de actualización vas a realizar.');
                return;
            }
            setStep(1);
            return;
        }
        if (step === 1) {
            if (!prepareReview()) return;
            setStep(2);
        }
    };

    const handleBack = () => {
        if (step === 0) return;
        setStep((s) => s - 1);
    };

    const handleSubmit = async () => {
        if (!prepareReview()) {
            setStep(1);
            return;
        }
        const rowsSnapshot = cloneRowsSnapshot(rows);
        const kindActive = updateKind || KIND_PRECIO;
        setIsSubmitting(true);
        try {
            if (updateKind === KIND_PRECIO) {
                const payload = buildPayloadPorCodigo(idSeller, rows, effectivePrecioFlags);
                const res = await postExcelPublicacionesModerador(payload);
                const data = res?.data ?? {};
                const errores = data?.errores ?? data?.errors ?? [];
                const list = Array.isArray(errores) ? errores : [];
                const enriched = enrichUpdateApiErrors(list, rowsSnapshot, KIND_PRECIO, []);
                const explicit = extractCreatedArraysFromUpdateResponse(data);
                let created = explicit.map((item) =>
                    typeof item === 'object' && item && !Array.isArray(item)
                        ? {
                              ...item,
                              label: formatApiValue(
                                  item?.skuMarket ??
                                      item?.SkuMarket ??
                                      item?.idPublicacion ??
                                      item?.IdPublicacion ??
                                      item?.codProducto ??
                                      item?.CodProducto,
                                  'OK'
                              )
                          }
                        : { label: formatApiValue(item, 'OK') }
                );
                if (!created.length) {
                    created = implicitCreatedRowsFromSnapshot(rowsSnapshot, enriched, KIND_PRECIO);
                }
                setResult({ created, errors: enriched });
                setStep(3);
                toastActualizacionBatchSummary(created.length, enriched.length, 'precio / stock');
                return;
            }
            if (updateKind === KIND_TIPO) {
                const actualizacionMasivaBulk = buildActualizacionMasivaBulkParcialItems(rows);
                if (!actualizacionMasivaBulk.length) {
                    toast.warn('No hay filas con datos para actualizar (además del IdPublicacion).');
                    return;
                }
                const res = await putActualizacionMasivaBulkParcialApi({
                    actualizacionMasivaBulk,
                    idUser: Number(idUsuario) || 0
                });
                const { created, errors } = normalizeTipoEnvioImagenResult(rowsSnapshot, KIND_TIPO, res?.data);
                setResult({ created, errors });
                setStep(3);
                toastActualizacionBatchSummary(created.length, errors.length, 'publicación');
                return;
            }
            const wb = buildWorkbookImagenes(rows);
            const b64 = workbookToBase64(wb);
            const res = await postMasivaImagenesExcelApi({ file: b64 });
            const { created, errors } = normalizeTipoEnvioImagenResult(rowsSnapshot, KIND_IMAGENES, res?.data);
            setResult({ created, errors });
            setStep(3);
            toastActualizacionBatchSummary(created.length, errors.length, 'imágenes');
        } catch (err) {
            const data = err?.response?.data;
            if (kindActive === KIND_TIPO || kindActive === KIND_IMAGENES) {
                const { created, errors } = normalizeTipoEnvioImagenResult(rowsSnapshot, kindActive, data);
                setResult({ created, errors });
                setStep(3);
                toastActualizacionBatchSummary(
                    created.length,
                    errors.length,
                    kindActive === KIND_TIPO ? 'publicación' : 'imágenes'
                );
            } else {
                const list = flattenBulkParcialErrorsList(data);
                const enriched = enrichUpdateApiErrors(list, rowsSnapshot, kindActive, []);
                const created = implicitCreatedRowsFromSnapshot(rowsSnapshot, enriched, kindActive);
                setResult({ created, errors: enriched });
                setStep(3);
                toastActualizacionBatchSummary(created.length, enriched.length, 'precio / stock');
            }
            const list = flattenBulkParcialErrorsList(data);
            if (!list.length) {
                toast.error('No se pudo completar la actualización.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (tipoUsuario === 6) {
        return null;
    }

    const safeRows = rows;
    const liveRowErrors = getLiveRowErrors(
        safeRows,
        updateKind || KIND_PRECIO,
        updateKind === KIND_PRECIO ? effectivePrecioFlags : undefined
    );
    const rowsWithIssues = liveRowErrors.filter((e) => Object.keys(e).length > 0).length;
    const totalPages = Math.max(1, Math.ceil(safeRows.length / ROWS_PER_PAGE));
    const visibleRows = safeRows.slice((currentRowsPage - 1) * ROWS_PER_PAGE, currentRowsPage * ROWS_PER_PAGE);
    const errorResults = Array.isArray(result?.errors) ? result.errors : [];
    const createdResults = Array.isArray(result?.created) ? result.created : [];

    const renderLoteInputs = (row, rowIndex) => {
        const globalIndex = (currentRowsPage - 1) * ROWS_PER_PAGE + rowIndex;
        const re = liveRowErrors[globalIndex] ?? {};

        if (updateKind === KIND_PRECIO) {
            return (
                <>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.skuMarket ? 'is-invalid' : ''}`}
                            value={row.skuMarket}
                            onChange={(e) => handleRowChange(row.id, 'skuMarket', e.target.value)}
                            placeholder="Pegá el SKU market (código de la publicación en marketplace)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.precioLista ? 'is-invalid' : ''}`}
                            value={row.precioLista}
                            onChange={(e) => handleRowChange(row.id, 'precioLista', e.target.value)}
                            placeholder="Precio de lista (solo número, ej. 1999.99)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.precioPromocional ? 'is-invalid' : ''}`}
                            value={row.precioPromocional}
                            onChange={(e) => handleRowChange(row.id, 'precioPromocional', e.target.value)}
                            placeholder="Precio promo (número, vacío si no aplica)"
                        />
                    </td>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.stock ? 'is-invalid' : ''}`}
                            value={row.stock}
                            onChange={(e) => handleRowChange(row.id, 'stock', e.target.value)}
                            placeholder="Stock disponible (entero, ej. 10)"
                        />
                    </td>
                </>
            );
        }
        if (updateKind === KIND_IMAGENES) {
            const imgPlaceholders = {
                img1: 'URL o ruta de la imagen principal (Img1)',
                img2: 'URL o ruta Img2 (opcional)',
                img3: 'URL o ruta Img3 (opcional)',
                img4: 'URL o ruta Img4 (opcional)',
                img5: 'URL o ruta Img5 (opcional)'
            };
            return (
                <>
                    <td>
                        <input
                            className={`form-control form-control-sm ${re.idPublicacion ? 'is-invalid' : ''}`}
                            value={row.idPublicacion}
                            onChange={(e) => handleRowChange(row.id, 'idPublicacion', e.target.value)}
                            placeholder="ID numérico de la publicación a actualizar"
                        />
                    </td>
                    {['img1', 'img2', 'img3', 'img4', 'img5'].map((f) => (
                        <td key={f}>
                            <input
                                className={`form-control form-control-sm ${re[f] ? 'is-invalid' : ''}`}
                                value={row[f]}
                                onChange={(e) => handleRowChange(row.id, f, e.target.value)}
                                placeholder={imgPlaceholders[f]}
                            />
                        </td>
                    ))}
                </>
            );
        }
        return (
            <>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.codProducto ? 'is-invalid' : ''}`}
                        value={row.codProducto}
                        onChange={(e) => handleRowChange(row.id, 'codProducto', e.target.value)}
                        placeholder="IdPublicacion (en plantilla puede figurar como CodProducto)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.skuSeller ? 'is-invalid' : ''}`}
                        value={row.skuSeller}
                        onChange={(e) => handleRowChange(row.id, 'skuSeller', e.target.value)}
                        placeholder="SkuSeller (opcional si ya informás otro identificador)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.skuMarket ? 'is-invalid' : ''}`}
                        value={row.skuMarket}
                        onChange={(e) => handleRowChange(row.id, 'skuMarket', e.target.value)}
                        placeholder="SkuMarket (opcional si ya informás otro identificador)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.observacion ? 'is-invalid' : ''}`}
                        value={row.observacion}
                        onChange={(e) => handleRowChange(row.id, 'observacion', e.target.value)}
                        placeholder="Observacion"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.titulo ? 'is-invalid' : ''}`}
                        value={row.titulo}
                        onChange={(e) => handleRowChange(row.id, 'titulo', e.target.value)}
                        placeholder="Titulo"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.ean ? 'is-invalid' : ''}`}
                        value={row.ean}
                        onChange={(e) => handleRowChange(row.id, 'ean', e.target.value)}
                        placeholder="EAN"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.linkYoutube ? 'is-invalid' : ''}`}
                        value={row.linkYoutube}
                        onChange={(e) => handleRowChange(row.id, 'linkYoutube', e.target.value)}
                        placeholder="LinkYoutube"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.descripcionAmpliada ? 'is-invalid' : ''}`}
                        value={row.descripcionAmpliada}
                        onChange={(e) => handleRowChange(row.id, 'descripcionAmpliada', e.target.value)}
                        placeholder="DescripcionAmpliada"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.idTipoPublicacion ? 'is-invalid' : ''}`}
                        value={row.idTipoPublicacion}
                        onChange={(e) => handleRowChange(row.id, 'idTipoPublicacion', e.target.value)}
                        placeholder="Opcional si no cambia — ID tipo publicación"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.envio ? 'is-invalid' : ''}`}
                        value={row.envio}
                        onChange={(e) => handleRowChange(row.id, 'envio', e.target.value)}
                        placeholder="Opcional — tipo de entrega (columna Envio del modelo)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.envioGratis ? 'is-invalid' : ''}`}
                        value={row.envioGratis}
                        onChange={(e) => handleRowChange(row.id, 'envioGratis', e.target.value)}
                        placeholder="EnvioGratis (1/0, true/false)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.categoria ? 'is-invalid' : ''}`}
                        value={row.categoria}
                        onChange={(e) => handleRowChange(row.id, 'categoria', e.target.value)}
                        placeholder="Categoria"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.marca ? 'is-invalid' : ''}`}
                        value={row.marca}
                        onChange={(e) => handleRowChange(row.id, 'marca', e.target.value)}
                        placeholder="Marca"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.idMotivoRevision ? 'is-invalid' : ''}`}
                        value={row.idMotivoRevision}
                        onChange={(e) => handleRowChange(row.id, 'idMotivoRevision', e.target.value)}
                        placeholder="IdMotivoRevision"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.observacionesRevision ? 'is-invalid' : ''}`}
                        value={row.observacionesRevision}
                        onChange={(e) => handleRowChange(row.id, 'observacionesRevision', e.target.value)}
                        placeholder="ObservacionesRevision"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.envioPropio ? 'is-invalid' : ''}`}
                        value={row.envioPropio}
                        onChange={(e) => handleRowChange(row.id, 'envioPropio', e.target.value)}
                        placeholder="EnvioPropio (1/0, true/false)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.codigoLegibilidad ? 'is-invalid' : ''}`}
                        value={row.codigoLegibilidad}
                        onChange={(e) => handleRowChange(row.id, 'codigoLegibilidad', e.target.value)}
                        placeholder="CodigoLegibilidad"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.garantiaExtActiva ? 'is-invalid' : ''}`}
                        value={row.garantiaExtActiva}
                        onChange={(e) => handleRowChange(row.id, 'garantiaExtActiva', e.target.value)}
                        placeholder="GarantiaExtActiva (1/0, true/false)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.garantiaFabrica ? 'is-invalid' : ''}`}
                        value={row.garantiaFabrica}
                        onChange={(e) => handleRowChange(row.id, 'garantiaFabrica', e.target.value)}
                        placeholder="GarantiaFabrica"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.promesaEntrega ? 'is-invalid' : ''}`}
                        value={row.promesaEntrega}
                        onChange={(e) => handleRowChange(row.id, 'promesaEntrega', e.target.value)}
                        placeholder="PromesaEntrega"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.amba ? 'is-invalid' : ''}`}
                        value={row.amba}
                        onChange={(e) => handleRowChange(row.id, 'amba', e.target.value)}
                        placeholder="Opcional — AMBA (0/1); solo esta zona si completás"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.interior ? 'is-invalid' : ''}`}
                        value={row.interior}
                        onChange={(e) => handleRowChange(row.id, 'interior', e.target.value)}
                        placeholder="Opcional — Interior (0/1)"
                    />
                </td>
                <td>
                    <input
                        className={`form-control form-control-sm ${re.patagonia ? 'is-invalid' : ''}`}
                        value={row.patagonia}
                        onChange={(e) => handleRowChange(row.id, 'patagonia', e.target.value)}
                        placeholder="Opcional — Patagonia (0/1)"
                    />
                </td>
            </>
        );
    };

    const theadPrecio = (
        <tr>
            <th>#</th>
            <th>SKU</th>
            <th>Precio lista</th>
            <th>Precio promo</th>
            <th>Stock</th>
            <th />
        </tr>
    );
    const theadImg = (
        <tr>
            <th>#</th>
            <th>IdPublicacion</th>
            <th>Img1</th>
            <th>Img2</th>
            <th>Img3</th>
            <th>Img4</th>
            <th>Img5</th>
            <th />
        </tr>
    );
    const theadTipo = (
        <tr>
            <th>#</th>
            <th>Id publicación</th>
            <th>SkuSeller</th>
            <th>SkuMarket</th>
            <th>Observacion</th>
            <th>Titulo</th>
            <th>EAN</th>
            <th>LinkYoutube</th>
            <th>DescripcionAmpliada</th>
            <th>IdTipoPublicacion</th>
            <th>Envio</th>
            <th>EnvioGratis</th>
            <th>Categoria</th>
            <th>Marca</th>
            <th>IdMotivoRevision</th>
            <th>ObservacionesRevision</th>
            <th>EnvioPropio</th>
            <th>CodigoLegibilidad</th>
            <th>GarantiaExtActiva</th>
            <th>GarantiaFabrica</th>
            <th>PromesaEntrega</th>
            <th>Amba</th>
            <th>Interior</th>
            <th>Patagonia</th>
            <th />
        </tr>
    );

    return (
        <div className="container wizard-publicaciones-page">
            <div className="wizard-publicaciones-shell">
                <div className="wizard-publicaciones-hero">
                    <div>
                        <span className="wizard-publicaciones-kicker">Actualización masiva</span>
                        <h2 className="wizard-publicaciones-title">Wizard para actualizar publicaciones</h2>
                    </div>
                    <div className="wizard-publicaciones-hero-actions">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/Publicaciones/ActualizacionMasivaExcel')}
                        >
                            Carga por Excel (legacy)
                        </button>
                        <button
                            type="button"
                            className="btn btn-light wizard-publicaciones-primary-light"
                            onClick={() => navigate('/Publicaciones')}
                        >
                            Ir a publicaciones
                        </button>
                    </div>
                </div>

                <div className="wizard-publicaciones-stepper">
                    {STEP_LABELS.map((label, index) => {
                        const stateClass =
                            index === step ? 'is-current' : index < step ? 'is-complete' : '';
                        return (
                            <div key={label} className={`wizard-publicaciones-step ${stateClass}`}>
                                <div className="wizard-publicaciones-step-node">{index + 1}</div>
                                <div className="wizard-publicaciones-step-label">{label}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="wizard-publicaciones-card">
                    {step === 0 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>1. Tipo de actualización</h3>
                                    <p>Elegí el tipo de actualización. Las columnas y validaciones coinciden con el Excel oficial.</p>
                                </div>
                            </div>
                            <div className="wizard-actualizacion-kind-grid">
                                {kindOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        disabled={opt.disabled}
                                        className={`wizard-actualizacion-kind-card ${
                                            updateKind === opt.id ? 'is-selected' : ''
                                        } ${opt.disabled ? 'is-disabled' : ''}`}
                                        onClick={() => !opt.disabled && selectUpdateKind(opt.id)}
                                    >
                                        <span className="wizard-actualizacion-kind-title">{opt.title}</span>
                                        <span className="wizard-actualizacion-kind-sub">{opt.subtitle}</span>
                                        {opt.disabled && (
                                            <span className="wizard-actualizacion-kind-badge">No disponible para tu perfil</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {showTemplateModal && (
                                <div className="wizard-template-modal-backdrop" role="dialog" aria-modal="true">
                                    <div className="wizard-template-modal">
                                        <h4>Ejemplo de excel</h4>
                                        <p>
                                            Descargá el formato de <strong>{meta?.title ?? 'actualización'}</strong> para usarlo
                                            como referencia o cargarlo en el paso siguiente.
                                        </p>
                                        <div className="wizard-template-modal-actions">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-green"
                                                onClick={() => downloadTemplateByKind(updateKind)}
                                            >
                                                Descargar formato
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={closeTemplateModalAndContinue}
                                            >
                                                Cerrar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 1 && meta && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>2. Actualización</h3>
                                    <p>
                                        Completá la grilla a mano o pegá desde Excel (primera fila con encabezados, mismo
                                        esquema que el modelo de la pantalla anterior).
                                    </p>
                                    {updateKind === KIND_TIPO && (
                                        <p className="text-muted small mb-0 mt-2">
                                            Actualización masiva parcial: podés mandar una sola columna por fila o varias; el
                                            backend no valida ni pide campos obligatorios del alta. Solo necesitamos
                                            IdPublicacion y al menos un dato a cambiar.
                                        </p>
                                    )}
                                </div>
                            </div>
                            {updateKind === KIND_PRECIO && (
                                <div className="wizard-publicaciones-panel mb-3">
                                    <h4>Opciones de actualización (vendedor / admin seller)</h4>
                                    <p className="text-muted small">
                                        Seller de la actualización: <strong>{idSeller ?? '—'}</strong>
                                        {idUsuario != null && (
                                            <>
                                                {' '}
                                                · Usuario: <strong>{idUsuario}</strong>
                                            </>
                                        )}
                                    </p>
                                    {isSellerOrAdmin && (
                                        <div className="wizard-publicaciones-switches">
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chPrecioLista}
                                                    onChange={(e) => setChPrecioLista(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar precio lista</span>
                                            </label>
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chPrecioPromo}
                                                    onChange={(e) => setChPrecioPromo(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar Precio promo</span>
                                            </label>
                                            <label className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={chStock}
                                                    onChange={(e) => setChStock(e.target.checked)}
                                                />
                                                <span className="form-check-label">Actualizar stock</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div key={csvDropZoneKey} className="wizard-actualizacion-csv-wrap mb-3">
                                <label className="form-label">Archivo CSV</label>
                                <div
                                    className={`wizard-actualizacion-csv-dropzone ${csvDragging ? 'is-dragging' : ''} ${
                                        csvJustLoaded ? 'is-loaded-flash' : ''
                                    }`}
                                    onDragEnter={onCsvDragEnter}
                                    onDragLeave={onCsvDragLeave}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={onCsvDrop}
                                    role="presentation"
                                >
                                    <input
                                        ref={csvInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="wizard-actualizacion-csv-input"
                                        onChange={onCsvInputChange}
                                    />
                                    <p className="wizard-actualizacion-csv-dropzone-title mb-1">
                                        Arrastrá y soltá un .csv aquí
                                    </p>
                                    <p className="wizard-actualizacion-csv-dropzone-sub mb-2">
                                        O{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-baseline"
                                            onClick={() => csvInputRef.current?.click()}
                                        >
                                            elegí un archivo
                                        </button>
                                        . Las filas se suman a la actualización al instante (mismos encabezados que al pegar).
                                    </p>
                                </div>
                            </div>

                            <div className="wizard-publicaciones-pastebox mb-3">
                                <label className="form-label">Pegar desde Excel (encabezado + filas, separador ; o tab)</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={pasteValue}
                                    onChange={(e) => setPasteValue(e.target.value)}
                                    placeholder={
                                        updateKind === KIND_PRECIO
                                            ? 'Primera fila: SKU;Precio Lista;Precio promo;Stock — debajo una fila por publicación'
                                            : updateKind === KIND_IMAGENES
                                              ? 'Primera fila: IdPublicacion;Img1;Img2;Img3;Img4;Img5 — URLs o rutas en cada columna'
                                              : 'Primera fila: IdPublicacion (o CodProducto) / SkuSeller / SkuMarket; y luego los campos a actualizar (vacíos se omiten)'
                                    }
                                />
                                <div className="wizard-publicaciones-pastebox-actions">
                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={applyPaste}>
                                        Agregar filas pegadas
                                    </button>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-table-toolbar">
                                <div className="wizard-publicaciones-table-meta">
                                    <strong>{safeRows.length}</strong> fila(s)
                                    <span>{rowsWithIssues} con datos a revisar</span>
                                    {safeRows.length > 0 && (
                                        <span>
                                            Página {currentRowsPage} de {totalPages}
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    <button type="button" className="btn btn-sm btn-light" onClick={addEmptyRow}>
                                        Agregar fila
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={removeCsvRows}>
                                        Quitar filas pegadas
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearRowsLote}>
                                        Vaciar actualización
                                    </button>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-table-pagination mb-2 d-flex gap-2 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentRowsPage === 1}
                                    onClick={() => setCurrentRowsPage((p) => Math.max(1, p - 1))}
                                >
                                    Anterior
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    disabled={currentRowsPage === totalPages}
                                    onClick={() => setCurrentRowsPage((p) => Math.min(totalPages, p + 1))}
                                >
                                    Siguiente
                                </button>
                            </div>
                            {safeRows.length === 0 ? (
                                <div className="wizard-publicaciones-empty-lote">
                                    <h4>La actualización está vacía</h4>
                                    <p>Pegá datos desde planilla o agregá una fila manual.</p>
                                </div>
                            ) : (
                                <div className="wizard-publicaciones-table-wrapper">
                                    <table className="table table-sm table-hover align-middle wizard-publicaciones-table">
                                        <thead>
                                            {updateKind === KIND_PRECIO
                                                ? theadPrecio
                                                : updateKind === KIND_IMAGENES
                                                  ? theadImg
                                                  : theadTipo}
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((row, idx) => {
                                                const gi = (currentRowsPage - 1) * ROWS_PER_PAGE + idx;
                                                const re = liveRowErrors[gi] ?? {};
                                                const issues = Object.entries(re).filter(([, m]) => m);
                                                return (
                                                    <tr
                                                        key={row.id}
                                                        data-row-id={row.id}
                                                        className={highlightErrorRowIds.includes(row.id) ? 'wizard-row-error-highlight' : ''}
                                                    >
                                                        <td>
                                                            <div className="wizard-publicaciones-row-index">
                                                                <strong>{gi + 1}</strong>
                                                                <span>{row.source === 'csv' ? 'Pegado' : 'Manual'}</span>
                                                                {highlightErrorRowIds.includes(row.id) && (
                                                                    <span className="wizard-row-error-badge">Error</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        {renderLoteInputs(row, idx)}
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-danger"
                                                                onClick={() => removeRow(row.id)}
                                                            >
                                                                Quitar
                                                            </button>
                                                            {issues.length > 0 && (
                                                                <div className="small text-danger mt-1">
                                                                    {issues.slice(0, 2).map(([k, m]) => (
                                                                        <div key={k}>{m}</div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 2 && meta && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>3. Revisión</h3>
                                    <p>Confirmá el envío: {safeRows.length} fila(s) lista(s) para actualizar.</p>
                                </div>
                            </div>
                            <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                <ul className="mb-0">
                                    <li>
                                        <strong>Tipo:</strong> {meta.title}
                                    </li>
                                    {updateKind === KIND_PRECIO && (
                                        <li>
                                            <strong>Campos marcados en contexto:</strong>{' '}
                                            {isSellerOrAdmin
                                                ? [chPrecioLista && 'Precio lista', chPrecioPromo && 'Precio promo', chStock && 'Stock']
                                                      .filter(Boolean)
                                                      .join(', ') || 'Ninguno'
                                                : 'Todos (perfil moderador)'}
                                        </li>
                                    )}
                                    <li>
                                        <strong>Seller (precio/stock):</strong> {idSeller ?? '—'}
                                    </li>
                                </ul>
                            </div>
                            <p className="text-muted small mt-3 mb-0">
                                {updateKind === KIND_TIPO
                                    ? 'Confirmás un envío parcial: 1 o varios campos por fila. El servicio no exige completar obligatorios del alta; lo que no mandás queda como está.'
                                    : 'Al confirmar se ejecuta la misma operación que la carga masiva por Excel en el backend.'}
                            </p>
                            {updateKind === KIND_TIPO && safeRows.length > 0 && (
                                <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft mt-3">
                                    <h4 className="mb-2">Vista rápida de filas a actualizar</h4>
                                    <ul className="wizard-publicaciones-results mb-0">
                                        {safeRows.slice(0, 20).map((row, index) => (
                                            <li key={`${row?.id ?? index}-${index}`}>
                                                <strong>{getTipoIdentifierLabel(row)}</strong>
                                                <span>{getTipoChangedFieldsLabel(row) || 'Sin cambios detectados'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {safeRows.length > 20 && (
                                        <p className="small text-muted mb-0 mt-2">
                                            Mostrando 20 de {safeRows.length} fila(s).
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {step === 3 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>4. Resultado</h3>
                                    <p>Detalle de la última ejecución.</p>
                                </div>
                            </div>
                            <div className="row g-3">
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Actualizaciones exitosas</h4>
                                        {createdResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty mb-0">
                                                {errorResults.length === 0
                                                    ? 'Proceso finalizado. Si el backend no devolvió detalle por fila, puede no haber lista explícita.'
                                                    : 'Revisá las filas con error al lado; las que no figuran allí se consideraron correctas.'}
                                            </p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results">
                                                {createdResults.map((item, index) => (
                                                    <li key={`${item?.label ?? item?.skuMarket ?? index}-${index}`}>
                                                        <span>
                                                            {formatApiValue(
                                                                item?.label ??
                                                                    item?.idPublicacion ??
                                                                    item?.skuMarket ??
                                                                    item?.codProducto,
                                                                'OK'
                                                            )}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Filas con error</h4>
                                        {errorResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty mb-0">
                                                No se reportaron errores por fila en esta ejecución.
                                            </p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results wizard-publicaciones-results-danger">
                                                {errorResults.map((item, index) => (
                                                    <li key={`${item?.__rowId ?? index}-${index}`}>
                                                        <strong>{getResultErrorHeading(item, index)}</strong>
                                                        <span>
                                                            {formatApiValue(
                                                                item?.errorMesage ??
                                                                    item?.ErrorMesage ??
                                                                    item?.message ??
                                                                    item?.Message ??
                                                                    (typeof item === 'string' ? item : ''),
                                                                'Sin detalle'
                                                            )}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="wizard-publicaciones-footer">
                        <div className="wizard-publicaciones-footer-note">
                            {step >= 3
                                ? 'Corregí las filas con error y reenviá, o iniciá una actualización nueva.'
                                : updateKind === KIND_TIPO && step >= 1
                                  ? 'Tipo/envío: revisamos formato básico en el navegador; el alta masiva parcial no valida obligatorios del ABM. Podés actualizar solo lo que cambies por fila.'
                                  : 'Validamos cada fila antes de revisar y enviar. Carga manual o pegado desde planilla.'}
                        </div>
                        <div className="wizard-publicaciones-footer-actions">
                            {step > 0 && step < 3 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleBack}
                                    disabled={isSubmitting}
                                >
                                    Volver
                                </button>
                            )}
                            {step > 0 && step < 2 && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleNext}
                                    disabled={isSubmitting}
                                >
                                    Continuar
                                </button>
                            )}
                            {step === 2 && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Enviando…' : 'Ejecutar actualización'}
                                </button>
                            )}
                            {step === 3 && (
                                <>
                                    {errorResults.length > 0 && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={returnToRowsForCorrection}
                                        >
                                            Volver a la actualización para corregir
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-outline-secondary" onClick={resetWizard}>
                                        Nueva actualización
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={() => navigate('/Publicaciones')}
                                    >
                                        Ir al listado
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                
            </div>
        </div>
    );
};

ActualizacionMasivaWizard.propTypes = {
    idSeller: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    idUsuario: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tipoUsuario: PropTypes.number,
    userType: PropTypes.shape({
        isSeller: PropTypes.bool,
        isSellerAdmin: PropTypes.bool,
        isAdminMod: PropTypes.bool,
        isCategory: PropTypes.bool,
        isAdmin: PropTypes.bool
    })
};

const mapStateToProps = (state) => ({
    idSeller: state.general.idSeller,
    userType: state.general.userType,
    idUsuario: state.general.userId,
    tipoUsuario: state.general.tipoUsuario
});

export default connect(mapStateToProps)(ActualizacionMasivaWizard);
